import { Button, Table, Tooltip } from "antd";
import ChantPopConfirm from "../../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./Chants.css";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import {
  fbDeleteChant,
  fbGetChants,
  fbUpdateChant,
} from "../../../../services/firebaseService/endPoints/admin/ourChants";
import {
  GUIDE_SETUP,
  TOPIC_ADD_CHANTS,
} from "../../../../constants/gettingStarted";
import { ArchiveIcon, EditIcon, UnarchiveIcon } from "../../../../utils/icons";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../services/firebaseService/connection";
import { invalidateStadiumCache, postUpdateCDNUtil } from "../../../../helperFunctions/util";
import logger from "../../../../utils/logger";
import { debounce } from "../../../../utils/helper";
import { updateUser } from "../../../../redux/actions/user";
import { useNavigate } from "react-router-dom";
import { fetchGroupDetails } from "../../../../services/firebaseService/endPoints/group";

const RowContext = React.createContext({});

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  const childrenArray = React.Children.toArray(props.children);
  if (childrenArray.length > 0) {
    childrenArray[0] = React.cloneElement(childrenArray[0], {});
  }

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {childrenArray}
      </tr>
    </RowContext.Provider>
  );
};

const Chants = ({
  archive,
  setChantsCount,
  setShowGlobalChantsSwitch,
  showGlobalChants,
}) => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [isLoading, setIsLoading] = useState(false);

  const updateChantsSortOrder = useCallback(
    async (updatedSortOrderIds) => {
      try {
        await updateDoc(doc(db, "group", groupData.id), {
          sortOrderChantIds: updatedSortOrderIds,
        });

        postUpdateCDNUtil(groupData.id);
        updateUser();
      } catch (error) {
        logger.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupData.id]
  );

  const debouncedUpdateChantsSortOrder = useMemo(
    () => debounce((ids) => updateChantsSortOrder(ids), 3000),
    [updateChantsSortOrder]
  );

  const debouncedInvalidateStadiumCache = useMemo(
    () => debounce(() => invalidateStadiumCache(groupData?.groupKey), 1000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupData?.groupKey]
  );

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: "move", width: "20px", padding: 0 }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const localChantsColumns = [
    {
      key: "sort",
      align: "center",
      width: 20,
      className: "header-white",
      render: (data) => {
        return data.type === "global" ? (
          ""
        ) : (
          <div>
            <DragHandle />
          </div>
        );
      },
    },
    {
      title: "Actions",
      className: "header-white",
      width: 50,
      align: "center",
      render: (data) => {
        return (
          <div className="flex-center" style={{ gap: "4px" }}>
            {!(data.type && data.type === "global") ? (
              <>
                <Tooltip title="edit">
                  <img
                    onClick={() => handleEditChant(data)}
                    src={EditIcon}
                    alt="edit-member"
                    style={{ width: 20, height: 20 }}
                  />
                </Tooltip>
                &nbsp;&nbsp;
                <ChantPopConfirm
                  title={"Delete chant?"}
                  onConfirm={() => {
                    handleDeleteChant(data);
                  }}
                  onCancel={() => {}}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="delete">
                    <DeleteOutlined style={{ fontSize: 18 }} />
                  </Tooltip>
                </ChantPopConfirm>
                &nbsp;&nbsp;
                <ChantPopConfirm
                  title={`${data.isPublished ? "Archive chant?" : "Publish chant?"}`}
                  onConfirm={() => {
                    handleChangePublish(data);
                  }}
                  onCancel={() => {}}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip
                    title={`${data.isPublished ? "archive" : "publish"}`}
                  >
                    <i className="fa cursor-pointer" aria-hidden="true">
                      {data.isPublished ? (
                        <img
                          src={ArchiveIcon}
                          style={{ width: 25, height: 25 }}
                          alt="Archive"
                        />
                      ) : (
                        <img
                          src={UnarchiveIcon}
                          style={{ width: 25, height: 25 }}
                          alt="Publish"
                        />
                      )}
                    </i>
                  </Tooltip>
                </ChantPopConfirm>
              </>
            ) : (
              <span style={{ fontWeight: 550, color: "#000000" }}>Global</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Name",
      width: 320,
      key: "title",
      className: "header-white",
      dataIndex: "title",
    },
    {
      title: "Video",
      width: 250,
      key: "videoLink",
      className: "header-white",
      dataIndex: "videoLink",
      render: (videoLink) => {
        let link = videoLink;
        if (link) {
          if (!`${link}`.includes("http")) {
            link = `https://${link}`;
          }
          return (
            <a href={link} target="_blank" rel="noopener noreferrer">
              Link
            </a>
          );
        }
        return <></>;
      },
    },
  ];

  const generateChantLists = (tableData, globalSortOrder) => {
    let localChantsList = [];
    let globalChantsList = [];

    const localSortOrder = groupData.sortOrderChantIds || [];

    tableData.forEach((item) => {
      if (item.type === "global") {
        const updatedItem = {
          ...item,
          sortIndex:
            (globalSortOrder || []).indexOf(item.id) !== -1
              ? (globalSortOrder || []).indexOf(item.id) + 1
              : (globalSortOrder || []).length + 1,
        };

        globalChantsList.push(updatedItem);
      } else {
        const updatedItem = {
          ...item,
          sortIndex:
            localSortOrder.indexOf(item.id) !== -1
              ? localSortOrder.indexOf(item.id) + 1
              : localSortOrder.length + 1,
        };

        localChantsList.push(updatedItem);
      }
    });

    localChantsList.sort((a, b) => a.sortIndex - b.sortIndex);
    globalChantsList.sort((a, b) => a.sortIndex - b.sortIndex);

    return { localChantsList, globalChantsList };
  };

  const [localChants, setLocalChants] = useState([]);
  const [globalChants, setGlobalChants] = useState([]);

  const getChants = async (groupId, archive, dashboardType) => {
    try {
      setIsLoading(true);

      const chantsList = await fbGetChants(groupId, !archive, dashboardType);

      let tableData = chantsList || [];

      if (tableData?.length > 0) {
        useAdminDataStore.getState().setGettingStartedItemStatus(
          groupId,
          GUIDE_SETUP,
          TOPIC_ADD_CHANTS,
          true
        );
      }

      let parentGroupData = null;
      const parentGroupId = groupData?.parents?.[0];

      if (parentGroupId && (groupData?.showGlobalChants ?? true)) {
        try {
          parentGroupData = await fetchGroupDetails(parentGroupId);
        } catch (error) {
          logger.error("Failed to fetch parent group details:", error);
        }
      }

      const { localChantsList, globalChantsList } = generateChantLists(
        tableData,
        parentGroupData?.sortOrderChantIds
      );

      if (
        groupData?.parents &&
        groupData?.parents?.length > 0 &&
        globalChantsList?.length > 0
      ) {
        setShowGlobalChantsSwitch(true);
      } else {
        setShowGlobalChantsSwitch(false);
      }

      setLocalChants(localChantsList);
      setGlobalChants(globalChantsList);

      setChantsCount(
        (localChantsList?.length || 0) + (globalChantsList?.length || 0)
      );
    } catch {
      logger.error("Failed to fetch chants.");
    } finally {
      setIsLoading(false);
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setLocalChants((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );

        const updatedSortOrder = arrayMove(prevState, activeIndex, overIndex);
        const updatedSortOrderIds = updatedSortOrder.map((item) => item.id);

        debouncedUpdateChantsSortOrder(updatedSortOrderIds);
        debouncedInvalidateStadiumCache();

        return updatedSortOrder;
      });
    }
  };

  const removeChantFromList = (id) => {
    setLocalChants((prev) => prev.filter((data) => data.id !== id));
    setIsLoading(false);
  };

  const handleChangePublish = (data) => {
    setIsLoading(true);

    fbUpdateChant(groupData.id, data.id, {
      isPublished: !data.isPublished,
    }).then(() => {
      removeChantFromList(data.id);
    });
  };

  const handleDeleteChant = (data) => {
    setIsLoading(true);

    fbDeleteChant(groupData.id, data.id).then(() => {
      removeChantFromList(data.id);
    });
  };

  const navigate = useNavigate();

  const handleEditChant = (data) => {
    navigate(`/admin/chants/edit/${groupData.id}/${data.id}`);
  };

  useEffect(() => {
    if (groupData?.id) {
      getChants(groupData?.id, archive, "admin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData.id, archive]);

  const tableData = useMemo(() => {
    if (showGlobalChants) {
      return [...localChants, ...globalChants];
    }
    return localChants;
  }, [globalChants, localChants, showGlobalChants]);

  return (
    <div className="chants-container">
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={localChants.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            bordered
            columns={localChantsColumns}
            components={{
              body: {
                row: Row,
              },
            }}
            loading={isLoading}
            pagination={false}
            dataSource={tableData}
            rowKey="id"
            scroll={{ x: "max-content" }}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default Chants;
